import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { Store } from '@ngxs/store';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { merge, Subscription, throwError } from 'rxjs';
import { catchError, filter, finalize } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { EventService, ToastService, ValidationService } from '../../core/services';
import { GiftShareValidators, NAME_REGEXP } from '../../shared';
import { UserProfile } from '../../core/models';
import { panelInOutAnimation } from '@core/animations';
import { formatDate, ViewportScroller } from '@angular/common';

@Component({
    selector: 'gs-event-edit',
    templateUrl: './event-edit.component.html',
    animations: [
        panelInOutAnimation
    ],
    standalone: false
})
export class EventEditComponent implements OnInit, OnDestroy {
  currentDate = new Date();

  tabs = {
    DETAILS: 0,
    IMAGE: 1,
    SETTINGS: 2
  };

  selectedTab: number = this.tabs.DETAILS;

  user: UserProfile;
  eventCode: string = '';

  eventTypes: any = [];
  eventTypesFormatted: any = [];

  editedImage: string = '';
  originalImage: string = '';

  form: FormGroup = this.formBuilder.group({
    eventTypeId: [null, [Validators.required]],
    name: [null, [Validators.required, Validators.maxLength(50)]],
    description: [null, [Validators.required, Validators.maxLength(100000)]],
    targetAmount: [null],
    deadlineDate: [null, [Validators.required]],
    isPublic: [false],
    isOnBehalf: [false],
    recipientName: [{ value: null, disabled: true }, [Validators.required, Validators.maxLength(100), Validators.pattern(NAME_REGEXP)]],
    recipientEmail: [{ value: null, disabled: true }, [Validators.required, GiftShareValidators.email, Validators.maxLength(256)]]
  });

  formImage: FormGroup = this.formBuilder.group({
    image: [null],
  });

  formSettings: FormGroup = this.formBuilder.group({
    hasTargetAmountHidden: [false, [Validators.required]],
    hasContributionsHidden: [false, [Validators.required]],
    hasCommentsHidden: [false, [Validators.required]],
    hasSuggestedAmount: [false, [Validators.required]],
    suggestedAmount: [{ value: null, disabled: true }, [Validators.required]],
    hasAcceptedTerms: [false, [Validators.requiredTrue]],
  });

  styleFormats = [
    {
      title: 'Headings', items: [
        { title: 'Heading 1', format: 'h1' },
        { title: 'Heading 2', format: 'h2' },
        { title: 'Heading 3', format: 'h3' },
        { title: 'Heading 4', format: 'h4' },
        { title: 'Heading 5', format: 'h5' },
        { title: 'Heading 6', format: 'h6' }
      ]
    },
    {
      title: 'Inline', items: [
        { title: 'Bold', format: 'bold' },
        { title: 'Italic', format: 'italic' },
        { title: 'Underline', format: 'underline' },
        { title: 'Strikethrough', format: 'strikethrough' }
      ]
    },
    {
      title: 'Align', items: [
        { title: 'Left', format: 'alignleft' },
        { title: 'Center', format: 'aligncenter' },
        { title: 'Right', format: 'alignright' },
        { title: 'Justify', format: 'alignjustify' }
      ]
    }
  ];

  private subscriptions: Subscription[] = [];

  get controls() {
    return this.form.controls;
  }

  get eventTypeId(): number {
    return this.form.controls.eventTypeId.value;
  }

  get eventType(): number {
    return this.eventTypes && this.eventTypes.find(x => x.id == this.eventTypeId);
  }

  get image(): string {
    return this.formImage.controls.image.value;
  }

  get nextLabel(): string {
    return this.selectedTab == this.tabs.SETTINGS ? this.eventCode ? 'Salveaza': 'Creeaza' : 'Inainte';
  }

  get backLabel(): string {
    return this.selectedTab == this.tabs.DETAILS ? 'Anuleaza': 'Inapoi';
  }

  constructor(
    private eventService: EventService,
    private ngxUiLoaderService: NgxUiLoaderService,
    private toastService: ToastService,
    private router: Router,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private store: Store,
    public vs: ValidationService,
    private viewportScroller: ViewportScroller,
  ) {

  }

  ngOnInit() {
    this.subscriptions.push(
      this.store.select<UserProfile>(state => state.auth.user)
        .subscribe(user => {
          this.user = user;
        }),

      this.store.select<any>(state => state.staticData.eventTypes)
        .pipe(filter(x => !!x))
        .subscribe((response: any) => {
          this.eventTypes = response.map(x => ({ ...x }));
          const options = [{
            id: 0,
            text: 'Selecteaza tip eveniment'
          }];

          this.eventTypesFormatted = options.concat(this.eventTypes.map(x => {
            let thumbnail = x.image.split('.');
            thumbnail = thumbnail[0] + '_thumbnail.' + thumbnail[1];
            return {
              id: x.id,
              text: x.name,
              image: environment.contentUrl + '/event-type-images/' + x.image,
              thumbnail: environment.contentUrl + '/event-type-images/' + thumbnail,
            };
          }));
        }),

      this.form.controls.isOnBehalf.valueChanges
        .subscribe(x => {
          if (x) {
            this.form.controls.recipientName.enable();
            this.form.controls.recipientEmail.enable();
          } else {
            this.form.controls.recipientName.disable();
            this.form.controls.recipientEmail.disable();
          }
        }),

      this.formSettings.controls.hasSuggestedAmount.valueChanges
        .subscribe(x => {
          if (x) {
            this.formSettings.controls.suggestedAmount.enable();
          } else {
            this.formSettings.controls.suggestedAmount.disable();
          }
        }),

      merge(
        this.form.controls.eventTypeId.valueChanges,
        this.formImage.controls.image.valueChanges
      ).subscribe(() => {
        this.updateEditorImage();
      })
    );

    this.eventCode = this.route.snapshot.paramMap.get('eventCode');
    const defaultEventTypeId = +this.route.snapshot.queryParamMap.get('eventTypeId');
    if (defaultEventTypeId) {
      this.form.patchValue({
        eventTypeId: defaultEventTypeId
      });
    }

    if (this.eventCode) {
      this.ngxUiLoaderService.start();
      this.subscriptions.push(
        this.eventService.getEventByCode(this.eventCode)
          .pipe(
            catchError(e => {
              this.toastService.checkErrorForStatus(e);
              return throwError(e);
            }),
            finalize(() => {
              this.ngxUiLoaderService.stop();
            })
          )
          .subscribe((event: any) => {

            if (this.user.id != event.userId) {
              this.router.navigate(['/forbidden']);
              return;
            }

            if (event.status == 'Completed') {
              this.router.navigate(['/error']);
              return;
            }

            if (event.status == 'Deleted') {
              this.router.navigate(['/error']);
              return;
            }

            if (!event) {
              this.router.navigate(['/eveniment', this.eventCode]);
              return;
            }

            this.form.patchValue({
              ...event,
              isOnBehalf: !!event.recipientName
            });

            this.formImage.patchValue({
              image: event.image
            });

            this.formSettings.patchValue({
              hasTargetAmountHidden: event.hasTargetAmountHidden,
              hasContributionsHidden: event.hasContributionsHidden,
              hasCommentsHidden: event.hasCommentsHidden,
              hasSuggestedAmount: event.hasSuggestedAmount,
              suggestedAmount: event.suggestedAmount || null,
              hasAcceptedTerms: true
            });

          })
      );
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  onNextClick() {
    switch (this.selectedTab) {
      case this.tabs.DETAILS: {
        if (!this.form.valid) {
          this.vs.triggerValidation(this.form);
          return;
        }

        this.onTabSelected(this.tabs.IMAGE);

        break;
      }
      case this.tabs.IMAGE: {
        if (!this.formImage.valid) {
          this.vs.triggerValidation(this.formImage);
          return;
        }

        this.onTabSelected(this.tabs.SETTINGS);
        break;
      }
      case this.tabs.SETTINGS: {
        this.onSubmit();
        break;
      }
    }
  }

  onBackClick() {
    switch (this.selectedTab) {
      case this.tabs.DETAILS: {
        this.router.navigate(['/user/dashboard']);
        break;
      }
      case this.tabs.IMAGE: {
        this.onTabSelected(this.tabs.DETAILS);
        break;
      }
      case this.tabs.SETTINGS: {
        this.onTabSelected(this.tabs.IMAGE);
        break;
      }
    }
  }

  /**
   * Create and Update events
   * @returns
   */
  onSubmit() {
    if (!this.form.valid ||
      !this.formImage.valid ||
      !this.formSettings.valid
    ) {
      this.vs.triggerValidation(this.form);
      this.vs.triggerValidation(this.formImage);
      this.vs.triggerValidation(this.formSettings);
      return;
    }

    const model = {
      ...this.form.value,
      ...this.formImage.value,
      ...this.formSettings.value,
      deadlineDate: formatDate(this.form.value.deadlineDate, 'yyyy-MM-dd', 'en')
    };

    if (this.eventCode) {
      this.ngxUiLoaderService.start();
      this.subscriptions.push(
        this.eventService.updateEvent(this.eventCode, model)
          .pipe(
            catchError(e => {
              this.toastService.show('Eroare', e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
              return throwError(e);
            }),
            finalize(() => {
              this.ngxUiLoaderService.stop();
            })
          )
          .subscribe(() => {
            this.toastService.show('Succes', 'Evenimentul a fost actualizat cu succes.', 'success');
            this.router.navigate(['/eveniment', this.eventCode]);
          })
      );
    } else {
      this.ngxUiLoaderService.start();
      this.subscriptions.push(
        this.eventService.createEvent(model)
          .pipe(
            catchError(e => {
              this.toastService.show('Eroare', e.error?.detail || e.error?.title || 'A aparut o problema neasteptata. Echipa noastra lucreaza pentru a o remedia.', 'error');
              return throwError(e);
            }),
            finalize(() => {
              this.ngxUiLoaderService.stop();
            })
          )
          .subscribe((event: any) => {
            this.toastService.show('Succes', 'Evenimentul a fost creat cu succes.', 'success');
            this.router.navigate(['/eveniment', event.code]);
          })
      );
    }
  }

  onTabSelected(id: number) {

    this.selectedTab = id;
    this.viewportScroller.scrollToPosition([0, 0]);
  }

  /**
   * Image Uploading Event handling.
   * @param {any} data
   */
  onImageSelected(data) {
    this.formImage.patchValue({
      image: data || null
    });
  }

  private updateEditorImage() {
    this.originalImage = this.getEventTypeImageFullUrl(this.eventType);
    this.editedImage = this.image ? environment.contentUrl + '/event-images/' + this.image : this.originalImage;
  }

  getEventTypeImageFullUrl(eventType: any): string {
    return eventType && environment.contentUrl + '/event-type-images/' + eventType.image;
  }

}
